import { isProductInCategory, products } from "./products";
import { getPackagingOptions } from "./packaging";

// Catalog-wide quantities confirmed by the user. Bottle exceptions refer to
// bottle products, not cleaning products that happen to offer bottle packaging.
const exceptions = {
  "KL-LDS-PF": { quantity: 150000, unit: "individual sheets" },
  "KL-LDS-VM": { quantity: 3500, unit: "retail package units" },
  "KL-LDPW-RG": { quantity: 3000, unit: "retail package units" },
  "KL-MPCPW-RG": { quantity: 3000, unit: "retail package units" },
  "KL-DDPW-RG": { quantity: 3000, unit: "retail package units" },
  "KL-TBCPW-RG": { quantity: 3000, unit: "retail package units" },
  "KL-LDP-RG": { quantity: 3000, unit: "retail package units" },
  "KL-DDP-RG": { quantity: 3000, unit: "retail package units" },
  "KL-DDP-PL": { quantity: 3000, unit: "retail package units" },
  "KL-LBP-RG": { quantity: 3000, unit: "retail package units" },
  "KL-SRS-RG": { quantity: 500, unit: "bottles" },
  "KL-SRW-RG": { quantity: 2000, unit: "boxes" },
  "KL-SBT-PL": { quantity: 500, unit: "bottles" },
  "KL-FBT-GL": { quantity: 500, unit: "bottles" },
  "KL-SBT-GL": { quantity: 500, unit: "bottles" },
};

const regularPackagingLabels = {
  drawerBox: "paper boxes",
  perforatedBox: "paper boxes",
  mailerBox: "paper boxes",
  kraftPaperBox: "paper boxes",
  paperBox: "paper boxes",
  paperEnvelope: "paper envelopes",
  plasticBox: "plastic boxes",
  paperCanister: "paper canisters",
  plasticBottle: "plastic bottles",
  plasticTube: "plastic tubes",
  clothBag: "cloth bags",
};

// Only actual catalog SKUs receive the default; unconfigured products and
// standalone section fixtures retain their explicitly supplied MOQ behavior.
export const moqBySku = Object.fromEntries(products.map(product => {
  const { sku } = product;
  const { quantity, unit } = exceptions[sku] || { quantity: 1000, unit: "retail package units" };
  const formattedQuantity = quantity.toLocaleString("en-US");
  const packagingOptions = getPackagingOptions(sku) || [];
  const hasResealablePouch = packagingOptions.some(option =>
    option.items.some(item => item.key === "resealablePouch"));
  // A pouch + box combination still belongs to the separate pouch MOQ tier.
  const regularItems = packagingOptions
    .filter(option => !option.items.some(item => item.key === "resealablePouch"))
    .flatMap(option => option.items);
  const regularLabels = [...new Set(regularItems.map(item => regularPackagingLabels[item.key]).filter(Boolean))];
  const regularNote = regularItems.some(item => item.key === "paperEnvelope")
    ? "MOQ per SKU. Paper envelopes are available for packs of up to 30 sheets."
    : regularItems.some(item => item.key === "candyWrapper")
      ? "MOQ per SKU for individually wrapped tablets in regular paper boxes."
      : "MOQ per SKU for regular packaging.";
  // Include sheet kits and the vending-machine sheet pack listed under accessories.
  const isSheetProduct = isProductInCategory(product, "sheets") || sku === "KL-LDS-VM";
  return [sku, {
    id: "moq",
    type: "moq",
    enabled: true,
    description: `MOQ starts at ${formattedQuantity} ${unit} per SKU.`,
    quantity: formattedQuantity,
    unit,
    scope: "Starting quantity per SKU",
    rows: [
      ...(sku === "KL-LDS-VM" ? [{
        id: "regular-packaging",
        configuration: "Small paper boxes",
        quantity: "3,500",
        unit: "units",
        note: "Each box contains 3–10 half-sheets. 1 unit = 1 box. MOQ per SKU.",
      }] : quantity === 1000 ? [{
        id: "regular-packaging",
        configuration: `Regular ${regularLabels.join(" or ") || "packaging"}`,
        quantity: "1,000",
        unit: "units",
        note: regularNote,
      }] : []),
      ...(isSheetProduct ? [{
        id: "custom-fragrance",
        configuration: "Custom fragrance",
        quantity: "150,000",
        unit: "individual sheets",
        note: sku === "KL-LDS-VM"
          ? "Per custom fragrance. The number of retail units depends on the number of sheets per paper box."
          : "Per custom fragrance. Retail package quantity depends on sheets per unit: 30 sheets per unit = 5,000 units; 50 sheets per unit = 3,000 units.",
      }] : []),
      ...(hasResealablePouch ? [{
        id: "resealable-pouch",
        configuration: "Resealable pouches",
        quantity: "3,000",
        unit: "pouches",
        note: sku === "KL-LDS-PF"
          ? `Packaging minimum only. Orders must also meet ${formattedQuantity} ${unit} per SKU; pouch quantity depends on sheets per pouch.`
          : "MOQ per SKU when resealable pouch packaging is selected.",
      }] : []),
    ],
    note: "Confirm the MOQ for your selected packaging and formulation in your project quotation.",
    cta: { label: "Confirm Your MOQ" },
  }];
}));
